/**
 * The three top-level areas of the app. The URL prefix is the permission
 * boundary — each area is protected by one SWA route rule pair in
 * staticwebapp.config.swa.json, and the same roles are repeated here so the
 * header only renders destinations the user can actually reach.
 *
 * Role names are canonical and come from users.json.
 */
export const ROLE_ADMIN = 'admin';
export const ROLE_AUTHOR = 'author';
export const ROLE_AUTHENTICATED = 'authenticated';

/** Roles worth showing a user. `anonymous`/`authenticated` say nothing they don't know. */
export const DISPLAY_ROLES: readonly string[] = [ROLE_ADMIN, ROLE_AUTHOR];

export type AreaKey = 'documents' | 'studio' | 'settings';

export interface AreaSection {
  to: string;
  /** i18n key for the tab label. */
  labelKey: string;
  /** Narrower than the area when set — system properties are admin-only inside Studio. */
  roles?: readonly string[];
}

export interface AppArea {
  key: AreaKey;
  /** Everything under this prefix belongs to the area. */
  prefix: string;
  /** Holding any one of these grants access. */
  roles: readonly string[];
  /**
   * Every destination in the area, in tab order. The header lands on the first
   * one the user can see, so a page listed here can never become unreachable.
   */
  sections: readonly AreaSection[];
}

export const APP_AREAS: readonly AppArea[] = [
  {
    key: 'documents',
    prefix: '/app',
    roles: [ROLE_AUTHENTICATED],
    sections: [
      { to: '/app/new', labelKey: 'library.picker.title' },
      { to: '/app/drafts', labelKey: 'drafts.title' }
    ]
  },
  {
    key: 'studio',
    prefix: '/studio',
    roles: [ROLE_ADMIN, ROLE_AUTHOR],
    sections: [
      { to: '/studio/templates', labelKey: 'studio.templates.title' },
      { to: '/studio/reference', labelKey: 'reference.title' },
      { to: '/studio/reference/categories', labelKey: 'studio.categories.link' },
      { to: '/studio/slide-library', labelKey: 'studio.slideLibrary.link' },
      { to: '/studio/claude-code', labelKey: 'studio.claudeCode.link' },
      {
        to: '/studio/reference/system',
        labelKey: 'settings.system.link',
        roles: [ROLE_ADMIN]
      }
    ]
  },
  {
    key: 'settings',
    prefix: '/settings',
    roles: [ROLE_ADMIN],
    sections: [
      { to: '/settings/services', labelKey: 'settings.services.title' },
      { to: '/settings/insights', labelKey: 'settings.insights.title' },
      { to: '/settings/users', labelKey: 'settings.users.title' }
    ]
  }
];

export function hasAnyRole(userRoles: readonly string[], anyOf: readonly string[]): boolean {
  return anyOf.some((role) => userRoles.includes(role));
}

export function visibleAreas(userRoles: readonly string[]): AppArea[] {
  return APP_AREAS.filter((area) => hasAnyRole(userRoles, area.roles));
}

export function activeArea(pathname: string): AppArea | undefined {
  return APP_AREAS.find(
    (area) => pathname === area.prefix || pathname.startsWith(`${area.prefix}/`)
  );
}

export function areaSections(area: AppArea, userRoles: readonly string[]): AreaSection[] {
  return area.sections.filter((section) => !section.roles || hasAnyRole(userRoles, section.roles));
}

export function areaHref(area: AppArea, userRoles: readonly string[]): string {
  return (areaSections(area, userRoles)[0] ?? area.sections[0]).to;
}
